import { loginRequest, msalConfig } from '../config/authConfig';
import { PublicClientApplication, AuthenticationResult, AccountInfo, EventType, InteractionRequiredAuthError } from '@azure/msal-browser';

// Create an MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

class AuthStore {
    private accessToken: string | null = null;
    private account: AccountInfo | null = null;
    private isLoginInProgress: boolean = false;

    async initialize(): Promise<void> {
        await msalInstance.initialize();

        // Enable account storage events to keep session synchronized across tabs
        msalInstance.enableAccountStorageEvents();
        msalInstance.addEventCallback((event) => {
            if (
                event.eventType === EventType.LOGIN_SUCCESS ||
                event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
                event.eventType === EventType.SSO_SILENT_SUCCESS
            ) {
                const payload = event.payload;
                if (payload !== null && 'account' in payload && payload.account !== undefined) {
                    const account = payload.account as AccountInfo;
                    msalInstance.setActiveAccount(account);
                    this.account = account;

                    if ('accessToken' in payload) {
                        this.accessToken = payload.accessToken;
                    }
                }
            }
        });

        let activeAccount = msalInstance.getActiveAccount();
        if (!activeAccount && msalInstance.getAllAccounts().length > 0) {
            activeAccount = msalInstance.getAllAccounts()[0];
            msalInstance.setActiveAccount(activeAccount);
        }

        this.account = activeAccount ?? null;
    }

    async acquireToken(): Promise<string | null> {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
            try {
                const response: AuthenticationResult = await msalInstance.acquireTokenSilent({
                    scopes: ['User.Read', 'Group.Read.All'],
                    account: accounts[0],
                });
                this.accessToken = response.accessToken;
                this.account = accounts[0];
                return this.accessToken;
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) {
                    if (!this.isLoginInProgress) {
                        this.isLoginInProgress = true;
                        void msalInstance.loginRedirect(loginRequest);
                    }
                    return null;
                } else {
                    console.error('Token acquisition failed', error);
                    return null;
                }
            }
        }
        return null;
    }

    getToken(): string | null {
        return this.accessToken;
    }

    getAccount(): AccountInfo | null {
        return this.account;
    }
}

// Export a singleton instance
const authStore = new AuthStore();
export { authStore, msalInstance };